import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Settings, Palette, Upload, Save, Loader2, Image as ImageIcon, Building2, Tag, Boxes, AlertCircle, Crown, Shield, Bell } from 'lucide-react';
import { salonApi } from '../../api/salonApi';
import { specialtyApi } from '../../api/specialtyApi';
import { inventoryCategoryApi } from '../../api/inventoryCategoryApi';
import { useSalonId } from '../../hooks/useSalonId';
import CatalogManager from '../../components/ui/CatalogManager';
import SubscriptionPanel from '../subscription/SubscriptionPanel';
import WhatsAppIntegration from './integrations/WhatsAppIntegration';
import FiscalSetupWizard from './integrations/FiscalSetupWizard';
import BookingSecurityConfigPanel from './BookingSecurityConfigPanel';
import BookingLinkPanel from './BookingLinkPanel';
import { useSubscription } from '../../hooks/useSubscription';
import DeleteAccountSection from './DeleteAccountSection';
import NotificationConfigPanel from './NotificationConfigPanel';
import { User } from 'lucide-react';


// Mock data for catalogs - replace with API calls
interface CatalogItemType {
    id: string;
    name: string;
    description?: string;
    color?: string;
    isActive: boolean;
}

const SettingsPage = () => {
    const salonId = useSalonId();
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isReloading, setIsReloading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const { isBetaMode, isFreeMode } = useSubscription();
    const isUnlimited = isBetaMode || isFreeMode;
    const [activeTab, setActiveTabRaw] = useState<'branding' | 'catalogs' | 'subscription' | 'integrations' | 'security' | 'account' | 'notifications'>('branding');

    // Sync tab with URL
    useEffect(() => {
        const tab = searchParams.get('tab');
        if (tab && ['branding', 'catalogs', 'subscription', 'integrations', 'security', 'account', 'notifications'].includes(tab)) {
            // Prevent access to subscription tab in Beta/Free Mode
            if (isUnlimited && tab === 'subscription') {
                setActiveTabRaw('branding');
                setSearchParams({ tab: 'branding' });
                return;
            }
            setActiveTabRaw(tab as any);
        }
    }, [searchParams, isUnlimited]);

    const setActiveTab = (tab: typeof activeTab) => {
        setActiveTabRaw(tab);
        setSearchParams({ tab });
    };

    // Form state
    const [name, setName] = useState('');
    const [address, setAddress] = useState('');
    const [primaryColor, setPrimaryColor] = useState('#0F766E');
    const [secondaryColor, setSecondaryColor] = useState('#2DD4BF');
    const [logoUrl, setLogoUrl] = useState<string | null>(null);
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);

    // Catalog state
    const [specialties, setSpecialties] = useState<CatalogItemType[]>([]);
    const [inventoryCategories, setInventoryCategories] = useState<CatalogItemType[]>([]);
    const [isCatalogsLoading, setIsCatalogsLoading] = useState(false);
    const [catalogError, setCatalogError] = useState<string | null>(null);

    // Preset color palettes for quick selection
    const colorPalettes = [
        { name: 'Esmeralda', primary: '#0F766E', secondary: '#2DD4BF' },
        { name: 'Rosa Elegante', primary: '#BE185D', secondary: '#F472B6' },
        { name: 'Oro Clásico', primary: '#B45309', secondary: '#FBBF24' },
        { name: 'Violeta', primary: '#7C3AED', secondary: '#A78BFA' },
        { name: 'Océano', primary: '#0369A1', secondary: '#38BDF8' },
        { name: 'Carbón', primary: '#374151', secondary: '#9CA3AF' },
    ];

    const fetchSalon = useCallback(async () => {
        if (!salonId) return;
        setIsLoading(true);
        try {
            const data = await salonApi.getSalonById(salonId);
            setName(data.name);
            setAddress(data.address || '');
            setPrimaryColor(data.primaryColor || '#0F766E');
            setSecondaryColor(data.secondaryColor || '#2DD4BF');
            setLogoUrl(data.logoUrl || null);
            setLogoPreview(data.logoUrl || null);
        } catch (err) {
            setError('Error al cargar la información del salón');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, [salonId]);

    useEffect(() => {
        fetchSalon();
    }, [fetchSalon]);

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const uploadLogo = async (): Promise<string | null> => {
        if (!logoFile || !salonId) return logoUrl;

        setIsUploading(true);
        try {
            // Get presigned URL
            const { uploadUrl, publicUrl } = await salonApi.getUploadUrl({
                fileName: logoFile.name,
                contentType: logoFile.type,
                salonId: salonId,
                folder: 'logos'
            });

            // Upload to R2
            const success = await salonApi.uploadFile(uploadUrl, logoFile);
            if (!success) throw new Error('Upload failed');

            return publicUrl;
        } catch (err) {
            console.error('Logo upload error:', err);
            throw new Error('Error al subir el logo');
        } finally {
            setIsUploading(false);
        }
    };

    const handleSave = async () => {
        if (!salonId) return;

        setIsSaving(true);
        setError(null);

        // --- VALIDATION START ---
        if (!name.trim()) {
            setError('El nombre del salón es obligatorio');
            setIsSaving(false);
            return;
        }
        // --- VALIDATION END ---

        try {
            // Upload logo first if changed
            let finalLogoUrl = logoUrl;
            if (logoFile) {
                finalLogoUrl = await uploadLogo();
            }

            // Update salon
            await salonApi.updateSalon(salonId, {
                name,
                address,
                primaryColor,
                secondaryColor,
                logoUrl: finalLogoUrl || undefined
            });

            setLogoUrl(finalLogoUrl);
            setLogoFile(null);

            // Show full-screen loading overlay and reload page
            setIsSaving(false);
            setIsReloading(true);

            // Reload after a short delay to show the overlay
            setTimeout(() => {
                window.location.reload();
            }, 500);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error al guardar');
            setIsSaving(false);
        }
    };

    const selectPalette = (primary: string, secondary: string) => {
        setPrimaryColor(primary);
        setSecondaryColor(secondary);
    };

    // Fetch catalogs from API
    const fetchCatalogs = useCallback(async () => {
        if (!salonId) return;
        setIsCatalogsLoading(true);
        setCatalogError(null);
        try {
            const [specialtiesData, categoriesData] = await Promise.all([
                specialtyApi.getSalonSpecialties(salonId),
                inventoryCategoryApi.getSalonCategories(salonId)
            ]);
            setSpecialties(specialtiesData);
            setInventoryCategories(categoriesData);
        } catch (err) {
            console.error('Error fetching catalogs:', err);
            setCatalogError('Error al cargar los catálogos');
        } finally {
            setIsCatalogsLoading(false);
        }
    }, [salonId]);

    // Load catalogs when switching to catalogs tab
    useEffect(() => {
        if (activeTab === 'catalogs') {
            fetchCatalogs();
        }
    }, [activeTab, fetchCatalogs]);

    // Catalog handlers - Specialties
    const handleAddSpecialty = async (item: Omit<CatalogItemType, 'id' | 'isActive'>) => {
        if (!salonId) return;
        try {
            const newSpecialty = await specialtyApi.createSpecialty({
                salonId,
                name: item.name,
                description: item.description
            });
            setSpecialties([...specialties, newSpecialty]);
        } catch (err) {
            console.error('Error creating specialty:', err);
            setCatalogError('Error al crear especialidad');
        }
    };

    const handleUpdateSpecialty = async (id: string, data: Partial<CatalogItemType>) => {
        try {
            const updated = await specialtyApi.updateSpecialty(id, {
                name: data.name,
                description: data.description,
                active: data.isActive
            });
            setSpecialties(specialties.map(s => s.id === id ? updated : s));
        } catch (err) {
            console.error('Error updating specialty:', err);
            setCatalogError('Error al actualizar especialidad');
        }
    };

    const handleDeleteSpecialty = async (id: string) => {
        try {
            await specialtyApi.deleteSpecialty(id);
            setSpecialties(specialties.filter(s => s.id !== id));
        } catch (err) {
            console.error('Error deleting specialty:', err);
            setCatalogError('Error al eliminar especialidad');
        }
    };

    // Catalog handlers - Inventory Categories
    const handleAddCategory = async (item: Omit<CatalogItemType, 'id' | 'isActive'>) => {
        if (!salonId) return;
        try {
            const newCategory = await inventoryCategoryApi.createCategory({
                salonId,
                name: item.name,
                description: item.description,
                color: item.color
            });
            setInventoryCategories([...inventoryCategories, newCategory]);
        } catch (err) {
            console.error('Error creating category:', err);
            setCatalogError('Error al crear categoría');
        }
    };

    const handleUpdateCategory = async (id: string, data: Partial<CatalogItemType>) => {
        try {
            const updated = await inventoryCategoryApi.updateCategory(id, {
                name: data.name,
                description: data.description,
                color: data.color
            });
            setInventoryCategories(inventoryCategories.map(c => c.id === id ? updated : c));
        } catch (err) {
            console.error('Error updating category:', err);
            setCatalogError('Error al actualizar categoría');
        }
    };

    const handleDeleteCategory = async (id: string) => {
        try {
            await inventoryCategoryApi.deleteCategory(id);
            setInventoryCategories(inventoryCategories.filter(c => c.id !== id));
        } catch (err) {
            console.error('Error deleting category:', err);
            setCatalogError('Error al eliminar categoría');
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-8">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="p-3 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl shadow-lg">
                        <Settings className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Configuración del Salón</h1>
                        <p className="text-gray-500">Personaliza la apariencia y catálogos</p>
                    </div>
                </div>

                {/* Tab Toggle - Responsive Grid (Mobile) / Flex (Desktop) */}
                <div className="w-full">
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:flex md:inline-flex bg-gray-100/80 backdrop-blur-sm p-1.5 rounded-xl border border-gray-200/50 gap-1 md:gap-0 min-w-full md:min-w-0">
                        {[
                            { id: 'branding', label: 'Marca', icon: Palette },
                            { id: 'catalogs', label: 'Catálogos', icon: Tag },
                            { id: 'integrations', label: 'Integraciones', icon: Boxes },
                            { id: 'subscription', label: 'Suscripción', icon: Crown },
                            { id: 'security', label: 'Seguridad', icon: Shield },
                            { id: 'notifications', label: 'Mensajes', icon: Bell },
                            { id: 'account', label: 'Cuenta', icon: User },
                        ].filter(tab => !isUnlimited || tab.id !== 'subscription').map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id as any)}
                                className={`flex items-center justify-center md:justify-start gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all w-full md:w-auto ${activeTab === tab.id
                                    ? 'bg-white text-gray-900 shadow-sm ring-1 ring-black/5'
                                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200/50'
                                    }`}
                            >
                                <tab.icon className={`w-4 h-4 ${activeTab === tab.id ? 'text-emerald-600' : 'text-gray-400'}`} />
                                <span className="truncate">{tab.label}</span>
                                {tab.id === 'subscription' && isUnlimited && (
                                    <span className="flex h-2 w-2 relative -ml-1">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
                    {error}
                </div>
            )}



            {activeTab === 'branding' ? (
                <>
                    {/* General Information Section */}
                    <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                        <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                            <div className="flex items-center gap-3">
                                <Building2 className="w-5 h-5 text-gray-600" />
                                <h2 className="text-lg font-semibold text-gray-900">Información General</h2>
                            </div>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Nombre del Salón
                                </label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                                    placeholder="Ej: Glamour Studio"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Dirección
                                </label>
                                <input
                                    type="text"
                                    value={address}
                                    onChange={(e) => setAddress(e.target.value)}
                                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                                    placeholder="Ej: Calle 100 #15-50, Bogotá"
                                />
                            </div>
                        </div>
                    </section>

                    {/* Branding Section */}
                    <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                        <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                            <div className="flex items-center gap-3">
                                <Palette className="w-5 h-5 text-gray-600" />
                                <h2 className="text-lg font-semibold text-gray-900">Marca y Colores</h2>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">Personaliza los colores y el logo para que la app refleje tu marca</p>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* Logo Upload */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                    Logo del Salón
                                </label>
                                <div className="flex items-start gap-6">
                                    {/* Preview */}
                                    <div
                                        className="w-32 h-32 rounded-2xl border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden bg-gray-50"
                                        style={{
                                            background: logoPreview ? 'white' : `linear-gradient(135deg, ${primaryColor}20, ${secondaryColor}20)`
                                        }}
                                    >
                                        {logoPreview ? (
                                            <img
                                                src={logoPreview}
                                                alt="Logo preview"
                                                className="w-full h-full object-contain p-2"
                                            />
                                        ) : (
                                            <ImageIcon className="w-10 h-10 text-gray-400" />
                                        )}
                                    </div>

                                    {/* Upload Button */}
                                    <div className="flex-1">
                                        <label className="relative cursor-pointer">
                                            <input
                                                type="file"
                                                accept="image/png, image/jpeg, image/webp"
                                                onChange={handleLogoChange}
                                                className="sr-only"
                                            />
                                            <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors w-fit">
                                                <Upload className="w-4 h-4" />
                                                <span className="text-sm font-medium">
                                                    {isUploading ? 'Subiendo...' : 'Subir Logo'}
                                                </span>
                                            </div>
                                        </label>
                                        <p className="text-xs text-gray-500 mt-2">
                                            PNG, JPG o WebP. Recomendado: 512x512px
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Color Palettes */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                    Paleta de Colores
                                </label>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                    {colorPalettes.map((palette) => (
                                        <button
                                            key={palette.name}
                                            onClick={() => selectPalette(palette.primary, palette.secondary)}
                                            className={`p-3 rounded-xl border-2 transition-all hover:scale-105 ${primaryColor === palette.primary
                                                ? 'border-gray-900 shadow-md'
                                                : 'border-gray-200 hover:border-gray-300'
                                                }`}
                                        >
                                            <div className="flex gap-1.5 mb-2">
                                                <div
                                                    className="w-6 h-6 rounded-full shadow-inner"
                                                    style={{ backgroundColor: palette.primary }}
                                                />
                                                <div
                                                    className="w-6 h-6 rounded-full shadow-inner"
                                                    style={{ backgroundColor: palette.secondary }}
                                                />
                                            </div>
                                            <span className="text-xs font-medium text-gray-700">{palette.name}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Custom Colors */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Color Primario
                                    </label>
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="color"
                                            value={primaryColor}
                                            onChange={(e) => setPrimaryColor(e.target.value)}
                                            className="w-12 h-12 rounded-xl cursor-pointer border-2 border-gray-200"
                                        />
                                        <input
                                            type="text"
                                            value={primaryColor}
                                            onChange={(e) => setPrimaryColor(e.target.value)}
                                            className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono uppercase"
                                            maxLength={7}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Color Secundario
                                    </label>
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="color"
                                            value={secondaryColor}
                                            onChange={(e) => setSecondaryColor(e.target.value)}
                                            className="w-12 h-12 rounded-xl cursor-pointer border-2 border-gray-200"
                                        />
                                        <input
                                            type="text"
                                            value={secondaryColor}
                                            onChange={(e) => setSecondaryColor(e.target.value)}
                                            className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono uppercase"
                                            maxLength={7}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Live Preview */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                    Vista Previa
                                </label>
                                <div
                                    className="p-6 rounded-2xl"
                                    style={{
                                        background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`
                                    }}
                                >
                                    <div className="flex items-center gap-4">
                                        {logoPreview ? (
                                            <img
                                                src={logoPreview}
                                                alt="Logo"
                                                className="w-12 h-12 rounded-xl bg-white p-1 object-contain"
                                            />
                                        ) : (
                                            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                                                <span className="text-white font-bold text-lg">
                                                    {name.charAt(0) || 'S'}
                                                </span>
                                            </div>
                                        )}
                                        <div>
                                            <h3 className="text-white font-bold text-lg">{name || 'Tu Salón'}</h3>
                                            <p className="text-white/80 text-sm">Vista previa de tu marca</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Save Button */}
                    <div className="flex justify-end pb-4">
                        <button
                            onClick={handleSave}
                            disabled={isSaving || isUploading}
                            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                        >
                            {isSaving ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Guardando...
                                </>
                            ) : (
                                <>
                                    <Save className="w-5 h-5" />
                                    Guardar Cambios
                                </>
                            )}
                        </button>
                    </div>
                </>
            ) : activeTab === 'catalogs' ? (
                /* Catalogs Tab */
                <div className="space-y-6 pb-8">
                    {/* Error Message */}
                    {catalogError && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 flex items-center gap-2">
                            <AlertCircle className="w-5 h-5" />
                            {catalogError}
                        </div>
                    )}

                    {/* Loading State */}
                    {isCatalogsLoading ? (
                        <div className="flex items-center justify-center py-16">
                            <div className="text-center">
                                <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-3" />
                                <p className="text-gray-500">Cargando catálogos...</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="grid md:grid-cols-2 gap-6">
                                {/* Specialties Catalog */}
                                <CatalogManager
                                    title="Especialidades"
                                    description="Habilidades de tus empleados"
                                    icon={<Tag className="w-5 h-5" />}
                                    accentColor="emerald"
                                    items={specialties}
                                    onAdd={handleAddSpecialty}
                                    onUpdate={handleUpdateSpecialty}
                                    onDelete={handleDeleteSpecialty}
                                    placeholder="Ej: Colorimetría, Trenzas, Barba..."
                                />

                                {/* Inventory Categories Catalog */}
                                <CatalogManager
                                    title="Categorías de Inventario"
                                    description="Organización de productos"
                                    icon={<Boxes className="w-5 h-5" />}
                                    accentColor="violet"
                                    items={inventoryCategories}
                                    onAdd={handleAddCategory}
                                    onUpdate={handleUpdateCategory}
                                    onDelete={handleDeleteCategory}
                                    showColorPicker={true}
                                    placeholder="Ej: Tintes, Shampoos, Accesorios..."
                                />
                            </div>

                            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-start gap-4">
                                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                                    <span className="text-xl">💡</span>
                                </div>
                                <div>
                                    <h4 className="font-semibold text-amber-900 mb-1">Consejo</h4>
                                    <p className="text-sm text-amber-800">
                                        Las especialidades que crees aquí estarán disponibles al crear empleados y servicios.
                                        Las categorías de inventario te ayudarán a organizar tus productos.
                                    </p>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            ) : activeTab === 'subscription' && !isUnlimited ? (
                /* Subscription Tab - Only visible when NOT in Beta Mode */
                <div className="pb-8">
                    <SubscriptionPanel
                        salonName={name || 'Mi Salón'}
                        userEmail={localStorage.getItem('userEmail') || ''}
                    />
                </div>
            ) : activeTab === 'integrations' ? (
                /* Integrations Tab */
                <div className="pb-8 space-y-12">
                    <WhatsAppIntegration />
                    <div className="border-t border-gray-100 pt-8">
                        <FiscalSetupWizard />
                    </div>
                </div>
            ) : activeTab === 'account' ? (
                /* Account Tab */
                <div className="pb-8">
                    <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                        <User className="w-6 h-6 text-gray-400" />
                        Gestión de Cuenta
                    </h2>
                    <DeleteAccountSection />
                </div>
            ) : activeTab === 'notifications' ? (
                /* Notifications Tab */
                <div className="pb-8">
                    <NotificationConfigPanel />
                </div>
            ) : (
                /* Booking Security Tab */
                <div className="pb-8 space-y-6">
                    {/* Link, QR & Embed Widget */}
                    <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                        <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-teal-50 to-white">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-teal-100 rounded-lg">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-teal-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                                </div>
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900">Link &amp; Widget de Reservas</h2>
                                    <p className="text-sm text-gray-500">Tu enlace personalizado, código QR y código para incrustar en tu web</p>
                                </div>
                            </div>
                        </div>
                        <div className="p-6">
                            <BookingLinkPanel salonName={name || ''} primaryColor={primaryColor} />
                        </div>
                    </section>

                    {/* Security Settings */}
                    <BookingSecurityConfigPanel />
                </div>
            )}

            {/* Full-screen Loading Overlay when applying changes */}
            {isReloading && (
                <div className="fixed inset-0 z-[100] bg-white/90 backdrop-blur-sm flex flex-col items-center justify-center">
                    <div className="text-center">
                        <Loader2 className="w-12 h-12 animate-spin text-emerald-600 mx-auto mb-4" />
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Aplicando cambios...</h3>
                        <p className="text-gray-500">Tu marca se está actualizando</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SettingsPage;

